import { Box } from '@mui/material'
import { FunctionComponent, ReactNode } from 'react'
import { motion } from 'motion/react'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { buildGaiaReportColorPalette, getReportChartSeriesColors } from '../../public-reports/utils/gaia-report-color-palette'
import { ReportContentProvider, ReportContentTokens } from '../../public-reports/shared-services/gaia-report-content'
import { GaiaReportWebPageClosing } from '../../public-reports/components/gaia-report-web-page-closing'
import type { IProjectReportWebpageData, IColorPalatte } from '../shared-services/project-reports.model'
import { ProjectReportWebPageHero } from './project-report-web-page-hero'
import { ProjectReportWebPageOverview } from './project-report-web-page-overview'
import { ProjectReportWebPageEmissions } from './project-report-web-page-emissions'
import { ProjectReportWebPageCategories } from './project-report-web-page-categories'
import { ProjectReportWebPageTimeline } from './project-report-web-page-timeline'
import { ProjectReportWebPageMethodology } from './project-report-web-page-methodology'

export const ProjectReportWebPage: FunctionComponent<{ report: IProjectReportWebpageData }> = ({ report }) => {
  const pdfExport = usePdfExportMode()
  const { organisationDetails, projectDetails, emissionDetails, excludedSections } = report

  const motionInitial = pdfExport ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
  const motionWhileInView = { opacity: 1, y: 0 }
  const motionTransition = pdfExport ? { duration: 0 } : { duration: 0.6, ease: 'easeOut' }

  const colorPalatte: IColorPalatte = {
    ...buildGaiaReportColorPalette(
      organisationDetails.organisationColor,
      organisationDetails.paletteOverrides ?? null
    ),
    chartSeries: getReportChartSeriesColors(
      organisationDetails.organisationColor,
      organisationDetails.paletteOverrides ?? null
    ),
  }

  const isSectionExcluded = (name: string) => (excludedSections ?? []).includes(name)

  const projectLabel = organisationDetails.screenLabels?.project?.singularCap ?? 'Project'

  const contentTokens: ReportContentTokens = {
    company: organisationDetails.organisationName,
    projectLabel,
    organisation: organisationDetails.screenLabels?.organisation?.singular ?? 'organisation',
  }

  const renderSection = (content: ReactNode, options?: { disableAnimation?: boolean }) => (
    <motion.div
      initial={options?.disableAnimation ? false : motionInitial}
      whileInView={motionWhileInView}
      viewport={{ once: true, margin: '-100px' }}
      transition={options?.disableAnimation ? { duration: 0 } : motionTransition}
      style={pdfExport ? { breakBefore: 'page' } : undefined}
    >
      {content}
    </motion.div>
  )

  const isDirectCustomer = !organisationDetails.partnerName

  return (
    <ReportContentProvider overrides={report.sectionContent} tokens={contentTokens}>
    <Box>
      <ProjectReportWebPageHero
        colorPalatte={colorPalatte}
        projectName={projectDetails.summary}
        organisationName={organisationDetails.organisationName}
        organisationLogoFilepath={organisationDetails.organisationLogoFilepath}
        coverImageFilepath={organisationDetails.coverImageFilepath}
        textColor={organisationDetails.textColor}
        customTitle={organisationDetails.customTitle}
        startDate={projectDetails.startDate}
        endDate={projectDetails.endDate}
        projectLabel={projectLabel}
      />

      {!isSectionExcluded('overview') &&
        renderSection(
          <ProjectReportWebPageOverview
            colorPalatte={colorPalatte}
            projectDetails={projectDetails}
            scopeTotals={emissionDetails.scopeTotals}
            projectLabel={projectLabel}
          />,
          { disableAnimation: true }
        )}

      {!isSectionExcluded('emissionsByScope') &&
        renderSection(
          <ProjectReportWebPageEmissions
            colorPalatte={colorPalatte}
            scopePieData={emissionDetails.scopePieData}
            projectLabel={projectLabel}
          />
        )}

      {!isSectionExcluded('emissionsByCategory') &&
        renderSection(
          <ProjectReportWebPageCategories
            colorPalatte={colorPalatte}
            categoryBarData={emissionDetails.categoryBarData}
            projectLabel={projectLabel}
          />,
          { disableAnimation: true }
        )}

      {!isSectionExcluded('emissionsOverTime') &&
        renderSection(
          <ProjectReportWebPageTimeline
            colorPalatte={colorPalatte}
            timelineData={emissionDetails.timelineData}
            projectLabel={projectLabel}
          />,
          { disableAnimation: true }
        )}

      {!isSectionExcluded('methodology') &&
        renderSection(
          <ProjectReportWebPageMethodology
            colorPalatte={colorPalatte}
            screenLabels={organisationDetails.screenLabels}
          />,
          { disableAnimation: true }
        )}

      {!isSectionExcluded('closing') &&
        renderSection(
          <GaiaReportWebPageClosing
            darkColor={colorPalatte.dark}
            partnerLogoFilepath={organisationDetails.partnerLogoFilepath}
            isDirectCustomer={isDirectCustomer}
          />,
          { disableAnimation: true }
        )}
    </Box>
    </ReportContentProvider>
  )
}
